/**
 * @ngdoc directive
 * @name flowForm
 * @module flowingly.runner.flow
 * @description  This comppnent is used to display the form the user uses to complete a flow step.
 * @usage
 * ```
    <flow-form ng-show="$ctrl.flow.form" flow="$ctrl.flow" is-mobile="$ctrl.isMobile"></flow-form>
 * ``` 
 * ### Notes
 * See Also: https://bizflo.atlassian.net/wiki/display/TECH/Angular+Flow+Components
 * ### Properties
 * #### Inputs
 * * flow: the flow data to display (JSON)
 * * isMobile: show mobile view if set
 * * showForm: display the form only if this is true
 */
import { FormGen } from '@Client/@types/formGen';
import { SharedAngular } from '@Client/@types/sharedAngular';
import { RunnerFlowsFormatterService } from '@Client/runner.services/flows.formatter';
import angular, {
  IFormController,
  IQService,
  IScope,
  ITimeoutService,
  IWindowService
} from 'angular';
import StepService, {
  IFormValidationResult
} from '@Client/runner.services/step.service';
import { FormFieldConditionAction } from '@Shared.Angular/flowingly.services/flowingly.constants';
import IFormattedFlow from '@Client/interfaces/IFormattedFlow';
import { Guid } from '@Shared.Angular/@types/guid';
import IFormattedStep from '@Client/interfaces/IFormattedStep';
import {
  IEventFileUploadResult,
  PubSubEvent
} from '@Shared.Angular/@types/pubSub';
import IFormInput from '@Shared.Angular/@types/core/contracts/queryModel/card/formInput';
import ActivityTaskType from '@Shared.Angular/@types/core/contracts/queryModel/workflow/steps/activityTaskType';
import { toCamelCaseDeep } from '@Shared.Angular/flowingly.services/case-converter';

class CustomError {
  private stack?: string;

  constructor(message?) {
    if (typeof (Error as any).captureStackTrace === 'function') {
      (Error as any).captureStackTrace(this, this.constructor);
    } else {
      this.stack = new Error(message).stack;
    }
  }
}

class UserCancelledException extends CustomError {}
class ValidationException extends CustomError {}

class FlowFormComponentController {
  static $inject = [
    '$window',
    '$scope',
    '$q',
    'intercomService',
    'notificationService',
    'pubsubService',
    'dialogService',
    'sessionService',
    'fgFileListService',
    'flowListManager',
    'flowApiService',
    'dateService',
    'flowinglyConstants',
    'flowinglyModelUtilityService',
    '$timeout',
    'runnerFlowsFormatter',
    'runnerCardService',
    'stepService',
    'appInsightsService',
    'busyService',
    'APP_CONFIG',
    'teamApiService'
  ];

  private flow: IFormattedFlow;
  private stepForForm: IFormattedStep;
  private isMobile;
  private _disableFooterButtons;

  private interval: any = 0;
  private completedApprovals: any;
  private numberOfCompletedApprovals: any;
  private numberOfApprovalsRequired: any;
  private numberOfApprovalsSelected: any;
  private flowForm: any;
  private subscriberId = 'runnerCardController';
  // currently only used to convert the pubsub pattern into
  // a promise so we can chain. The best case scenario would be to
  // avoid the pubsub pattern for now as it is currently adding an extra
  // layer of unecessary complexity
  //                                                      - Cassey
  private deferredFileUploadPromise: angular.IDeferred<any>[];
  private stepTagIds: Guid[] = [];
  public showEmptyTeamWarning = false;
  private enableStepTags: boolean;

  constructor(
    private $window: IWindowService,
    private $scope: IScope,
    private $q: IQService,
    private intercomService: SharedAngular.IntercomService,
    private notificationService: SharedAngular.NotificationService,
    private pubsubService: SharedAngular.PubSubService,
    private dialogService: SharedAngular.DialogService,
    private sessionService: SharedAngular.SessionService,
    private fgFileListService: FormGen.FgFileListService,
    private flowListManager: FlowListManager,
    private flowApiService: FlowApiService,
    private dateService: DateService,
    private flowinglyConstants: SharedAngular.FlowinglyConstants,
    private flowinglyModelUtilityService: SharedAngular.FlowinglyModelUtilityService,
    private $timeout: ITimeoutService,
    private runnerFlowsFormatter: RunnerFlowsFormatterService,
    private runnerCardService: RunnerCardService,
    private stepService: StepService,
    private appInsightService: SharedAngular.AppInsightsService,
    private busyService: SharedAngular.BusyService,
    private APP_CONFIG: SharedAngular.APP_CONFIG,
    private teamApiService: SharedAngular.TeamApiService
  ) {
    // a number of these are being used as callbacks, but I'm too tired to figure
    // out which functions are which, so I'm just going to blindly bind all of them
    // to this
    //                                                              - Cassey
    this.sendCard = this.sendCard.bind(this);
    this.isFormValid = this.isFormValid.bind(this);
    this.getDynamicActorsAndApproversThatShouldBeSelected =
      this.getDynamicActorsAndApproversThatShouldBeSelected.bind(this);
    this.getModelerNodeIdForStep = this.getModelerNodeIdForStep.bind(this);
    this.getStepForModelerNode = this.getStepForModelerNode.bind(this);
    this.getStepForStepId = this.getStepForStepId.bind(this);
    this.getFlowSchemaForStep = this.getFlowSchemaForStep.bind(this);
    this.getNextModelerNodeIds = this.getNextModelerNodeIds.bind(this);
    this.isApprovalTaskRejected = this.isApprovalTaskRejected.bind(this);
    this.addNodeIdsForNodes = this.addNodeIdsForNodes.bind(this);
    this.findMatchedGate = this.findMatchedGate.bind(this);
    this.compute = this.compute.bind(this);
    this.getDecisionFieldValueForExclusiveGateway =
      this.getDecisionFieldValueForExclusiveGateway.bind(this);
    this.getNextNodeIdsForExclusiveGateway =
      this.getNextNodeIdsForExclusiveGateway.bind(this);
    this.multipleApproverDialog = this.multipleApproverDialog.bind(this);
    this.multipleApproversDialog = this.multipleApproversDialog.bind(this);
    this.dynamicActorsDialog = this.dynamicActorsDialog.bind(this);
    this.completeStep = this.completeStep.bind(this);
    this.isElementInViewport = this.isElementInViewport.bind(this);
    this.shouldNotSave = this.shouldNotSave.bind(this);
    this.saveFormProgress = this.saveFormProgress.bind(this);
    this.disableFooterButtons = this.disableFooterButtons.bind(this);
    this.enableFooterButtons = this.enableFooterButtons.bind(this);
    this.fileRemovalComplete = this.fileRemovalComplete.bind(this);
    this.setFileError = this.setFileError.bind(this);
    this.clearFileError = this.clearFileError.bind(this);
    this.settingOffsettoForm = this.settingOffsettoForm.bind(this);
    this.getStepTagIds = this.getStepTagIds.bind(this);
    if (this.flow?.finalisedDate === null) {
      $window.onbeforeunload = () => {
        //returning true prompts the user with a stndard browser dialog.
        //it asks them to confirm they want to leave /refresh the page as data will be lost
        //You might ask, why not just call saveFormProgress() here? Good question.
        //I tried that first, but for some reason despite being called, it did not actually save.
        //Did not have time to investigate why, so this is a quicker solution.
        if (this.flowForm && this.flowForm.$dirty) {
          return true;
        } else {
          return null;
        }
      };
    }

    this.initializeDeferredFileUploadPromise();
  }

  $onChanges(changes) {
    this.enableFooterButtons();
    if (changes.stepForForm) {
      const stepIdChanged =
        changes.stepForForm.currentValue?.id !==
        changes.stepForForm.previousValue?.id;
      const actorChanged =
        changes.stepForForm.currentValue?.actorAssignedId !==
        changes.stepForForm.previousValue?.actorAssignedId;

      const previousStep = changes.stepForForm.previousValue;

      // Preserve unsaved changes across SignalR step refresh for diverge step.
      const userHasUnsavedChanges =
        !stepIdChanged && !actorChanged && this.flowForm?.$dirty;

      if (userHasUnsavedChanges && previousStep?.$data) {
        this.stepService.preserveUnsavedFormDataInFields(
          previousStep.$data,
          this.stepForForm
        );
      }
      this.stepService.initialiseFormData(this.stepForForm);

      this.completedApprovals = this.stepForForm.completedApprovals;
      this.numberOfCompletedApprovals = this.completedApprovals.length;
      this.interval = this.$timeout(this.settingOffsettoForm);
      this.stepTagIds = this.getStepTagIds();
      if (this.flowForm && !userHasUnsavedChanges) {
        // Reset the form because it is reused between parallel steps.
        this.flowForm.$setPristine();
      }

      if (stepIdChanged || actorChanged) {
        this.checkForEmptyTeam();
      }
    }
  }

  $onInit() {
    this.pubsubService.subscribe(
      'FILEUPLOAD_UPLOAD_STARTED',
      this.disableFooterButtons,
      this.subscriberId
    ); // YUCK!!!
    this.pubsubService.subscribe(
      'FILEUPLOAD_FILE_ERROR',
      this.setFileError,
      this.subscriberId
    );
    this.pubsubService.subscribe(
      'FILEUPLOAD_REMOVAL_STARTED',
      this.disableFooterButtons,
      this.subscriberId
    ); // YUCK!!!
    this.pubsubService.subscribe(
      'FILEUPLOAD_FILE_VALID',
      this.clearFileError,
      this.subscriberId
    );
    this.pubsubService.subscribe(
      'FILEUPLOAD_UPLOAD_COMPLETED',
      this.enableFooterButtons,
      this.subscriberId
    );
    this.pubsubService.subscribe(
      'FILEUPLOAD_REMOVAL_COMPLETED',
      this.fileRemovalComplete,
      this.subscriberId
    );
    this.pubsubService.subscribe(
      'FILEUPLOAD_UPLOAD_FAILED',
      this.enableFooterButtons,
      this.subscriberId
    );

    this.enableStepTags = this.APP_CONFIG.enableTagging;
  }

  $onDestroy() {
    this.$window.onbeforeunload = null;
    //avoid nuisance calllbacks and memory leaks
    this.pubsubService.unsubscribeAll(this.subscriberId);
    this.saveFormProgress();
    this.fgFileListService.removeAllFileControls();
  }

  private checkForEmptyTeam() {
    this.showEmptyTeamWarning = false;

    if (!this.stepForForm || !this.stepForForm.actorAssignedId || !this.flow) {
      return;
    }

    this.teamApiService
      .validateTeam(this.stepForForm.actorAssignedId)
      .then((result) => {
        this.showEmptyTeamWarning =
          result.isValidTeamId && result.numberOfUsers === 0;
      });
  }

  public onSubmitCb() {
    this.appInsightService.startEventTimer('stepFormSubmitted');
    this.appInsightService.startEventTimer('stepFormTransitionStarted');
    const formData = angular.copy((this.stepForForm as any).$data);
    this.runnerCardService.formatFieldsWithCustomDBUserTypeToObject(
      formData,
      this.stepForForm.schema
    );
    const fileUploadData = this.runnerCardService.createFileUploadData(
      this.stepForForm.id,
      this.stepForForm.schema
    );

    return this.sendCard(
      this.stepForForm.id,
      this.stepForForm.flowId,
      formData,
      this.stepForForm.schema,
      this.flowForm,
      fileUploadData
    );
  }

  public sendCard(
    stepId,
    flowId,
    data,
    schema,
    form: IFormController,
    fileUploadData?
  ) {
    let validationResult: IFormValidationResult;
    if (this.busyService.isBusy == true) {
      return this.$q.reject('Saving in progress...');
    }

    const stepSubmissionPromise = this.$q
      .resolve()
      .then(() => {
        validationResult = this.stepService.validateForm(
          flowId,
          data,
          schema,
          form,
          fileUploadData,
          true,
          stepId
        );
        if (!this.isFormValid(validationResult)) {
          throw new ValidationException();
        } else {
          this.runnerFlowsFormatter.encodeFlowFormData(
            validationResult.formData,
            schema.fields,
            false
          );
        }

        const visibleFieldsFormData = JSON.parse(JSON.stringify(data));
        validationResult.formData.forEach((item) => {
          if (item?.fieldConditionStatus === FormFieldConditionAction.Hide) {
            delete visibleFieldsFormData[item.key];
          }
        });

        return this.saveFormProgress(true).then(() =>
          this.getDynamicActorsAndApproversThatShouldBeSelected(
            stepId,
            flowId,
            visibleFieldsFormData
          )
        );
      })
      .then((result) => {
        const hasDynamicActors =
          result.dynamicActors &&
          result.dynamicActors.length > 0 &&
          result.dynamicActors.some((da) => da.dynamicActors.length !== 0);
        const hasMultipleApprovers =
          result.multipleApprovers && result.multipleApprovers.length > 0;
        const completeStepPayload: ICompleteStepPayload = {
          form,
          stepId,
          flowId,
          validatedData: validationResult,
          assignedDynamicActors: null,
          selectedApprovers: null
        };

        let payloadBuilderPromises = this.$q.resolve(completeStepPayload);

        if (hasDynamicActors) {
          payloadBuilderPromises = payloadBuilderPromises.then(
            (completeStepPayload) => {
              const nextModelerNodeIds = this.getNextModelerNodeIds(
                stepId,
                this.flow,
                data
              );
              const modelerNodeId = this.getModelerNodeIdForStep(
                stepId,
                this.flow
              );

              const timedDynamicActorsDialog =
                this.appInsightService.timeUserActivityForEvent(
                  this.dynamicActorsDialog,
                  ['stepFormSubmitted', 'stepFormTransitionStarted']
                );
              return timedDynamicActorsDialog(
                stepId,
                flowId,
                result.dynamicActors,
                nextModelerNodeIds,
                modelerNodeId
              ).then((assignedDynamicActors) => {
                if (
                  !assignedDynamicActors ||
                  assignedDynamicActors.length === 0
                ) {
                  throw new UserCancelledException();
                } else {
                  completeStepPayload.assignedDynamicActors =
                    assignedDynamicActors;
                }
                return completeStepPayload;
              });
            }
          );
        }

        if (hasMultipleApprovers) {
          payloadBuilderPromises = payloadBuilderPromises.then(
            (completeStepPayload) => {
              const nextModelerNodeIdsWithoutCrossingMergeGw =
                this.getNextModelerNodeIds(stepId, this.flow, data, true);

              const timedMultipleApproversDialog =
                this.appInsightService.timeUserActivityForEvent(
                  this.multipleApproversDialog,
                  ['stepFormSubmitted', 'stepFormTransitionStarted']
                );

              return timedMultipleApproversDialog(
                form,
                flowId,
                stepId,
                validationResult,
                result.multipleApprovers,
                nextModelerNodeIdsWithoutCrossingMergeGw
              ).then((selectedApprovers: unknown[]) => {
                if (!selectedApprovers || selectedApprovers.length === 0) {
                  throw new UserCancelledException();
                } else {
                  completeStepPayload.selectedApprovers = selectedApprovers;
                }

                return completeStepPayload;
              });
            }
          );
        }

        return payloadBuilderPromises;
      })
      .then(this.completeStep)
      .catch((e) => {
        if (e instanceof UserCancelledException) {
          console.info('[User] Cancelled dynamic/multiple actor selection');
        } else if (e instanceof ValidationException) {
          console.info('[System] Failed validation');
          this.notificationService.showErrorToast(
            'Oops, a validation error has occurred, please review the fields on the current step for more details.'
          );
        } else {
          console.error(e, stepId, flowId, data, schema, form);
          this.notificationService.showErrorToast(
            'Sorry, there is an issue when trying to complete this step.'
          );
        }
      })
      .then(() => {
        //Make sure the DB has saved the step information so that SaveInProgress is not
        //called while this transaction is taking place
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return this.$timeout(() => {}, 300);
      });

    this.busyService.addPromise(stepSubmissionPromise);

    return stepSubmissionPromise;
  }

  private isFormValid(validationResult: IFormValidationResult) {
    const isValid = this.stepService.validationResultIsGood(validationResult);

    if (isValid) {
      //Last line of defence for validating the form
      if (!validationResult.form.$valid) {
        return false;
      }
      return true;
    } else {
      return false;
    }
  }

  private getDynamicActorsAndApproversThatShouldBeSelected(
    stepId: Guid,
    flowId: Guid,
    formInputs: object
  ) {
    const modelerNodeId = this.getModelerNodeIdForStep(stepId, this.flow);
    const inputsWithFileIds = this.replaceFileObjectsWithIds(
      formInputs,
      stepId
    );
    const options = {
      flowId: flowId,
      stepId: stepId,
      modelerNodeId: modelerNodeId,
      maxNumberOfUsersToShow: this.flowinglyConstants.maxNumberOfUsersToShow,
      data: JSON.stringify(inputsWithFileIds),
      searchTerm: null
    };
    return this.flowApiService.getApproversAndDynamicActorsForNodes(options);
  }

  private replaceFileObjectsWithIds(data: object, stepId: Guid) {
    const formInputs = Object.entries(data).map((kvp) => ({
      key: kvp[0],
      value: kvp[1]
    })) as IFormInput[];
    const files = this.fgFileListService.getFilesForStep(stepId);
    const inputsWithFileIds = this.runnerCardService.addFileIdsToForm(
      formInputs,
      files
    );
    return Object.fromEntries(inputsWithFileIds.map((i) => [i.key, i.value]));
  }

  private getModelerNodeIdForStep(stepId, flow) {
    if (flow && flow.steps) {
      const step = flow.steps.find((s) => s.id === stepId);
      return step && step.modelerNodeId;
    }
    return null;
  }

  private getStepForModelerNode(nodeId, flow) {
    if (flow && flow.steps) {
      const steps = flow.steps
        .filter((s) => s.modelerNodeId === nodeId)
        .sort((a, b) => (a.completedDate < b.completedDate ? 1 : -1));
      if (steps && steps.length > 0) {
        return steps[0];
      }
    }
    return null;
  }

  private getStepForStepId(stepId, flow) {
    if (flow && flow.steps) {
      const step = flow.steps.find((s) => s.id === stepId);
      return step;
    }
    return null;
  }

  private getFlowSchemaForStep(stepId, flow) {
    const step = flow.steps.find((s) => s.id === stepId);
    if (step && !!step.inComponentSchemaId) {
      const componentSchema = flow.flowComponentSchemas.find(
        (s) => s.schemaId === step.inComponentSchemaId
      );
      if (componentSchema && componentSchema.flowSchema)
        return toCamelCaseDeep(JSON.parse(componentSchema.flowSchema));
    }

    return toCamelCaseDeep(JSON.parse(flow.parsedSchema));
  }

  private getNextModelerNodeIds(stepId, flow, data, stopAtMergeGateway?) {
    if (this.isApprovalTaskRejected(stepId, flow, data)) return null;

    const modelerNodeId = this.getModelerNodeIdForStep(stepId, flow);
    const flowSchema = this.getFlowSchemaForStep(stepId, flow);
    const nodeKey = this.flowinglyModelUtilityService.getNodeKeyById(
      flowSchema.nodeDataArray,
      modelerNodeId
    );
    const nextModelNodes =
      this.flowinglyModelUtilityService.getNextNodesForNode(
        nodeKey,
        flowSchema.nodeDataArray,
        flowSchema.linkDataArray
      );
    const modelerNodeIds = [];
    this.addNodeIdsForNodes(
      nextModelNodes,
      flowSchema,
      data,
      flow,
      modelerNodeIds,
      stopAtMergeGateway
    );

    return modelerNodeIds;
  }

  private isApprovalTaskRejected(stepId, flow, data) {
    const step = this.getStepForStepId(stepId, flow);
    if (
      step.stepType === ActivityTaskType.APPROVAL ||
      step.stepType === ActivityTaskType.PARALLEL_APPROVAL ||
      step.stepType === ActivityTaskType.SEQUENTIAL_APPROVAL
    ) {
      const approvalField = step.fields.find(
        (f) =>
          f.type.toLowerCase() ===
          this.flowinglyConstants.stepTypeName.APPROVAL.toLowerCase()
      );
      const approvalValue = data[approvalField.name];
      if (
        approvalValue != null &&
        approvalValue !== this.flowinglyConstants.approvalTaskOptions.APPROVE
      ) {
        return true;
      }
    }

    return false;
  }

  private addNodeIdsForNodes(
    modelNodes,
    flowSchema,
    data,
    flow,
    modelerNodeIds,
    stopAtMergeGateway?
  ) {
    (modelNodes || []).forEach((node) => {
      let nextNodes = [];
      switch (node.category) {
        case this.flowinglyConstants.nodeCategory.ACTIVITY:
        case this.flowinglyConstants.nodeCategory.COMPONENT:
          modelerNodeIds.push(node.id);
          break;
        case this.flowinglyConstants.nodeCategory.DIVERGE_GATEWAY:
        case this.flowinglyConstants.nodeCategory.CONVERGE_GATEWAY:
          if (!stopAtMergeGateway) {
            nextNodes = this.flowinglyModelUtilityService.getNextNodesForNode(
              node.key,
              flowSchema.nodeDataArray,
              flowSchema.linkDataArray
            );
            this.addNodeIdsForNodes(
              nextNodes,
              flowSchema,
              data,
              flow,
              modelerNodeIds,
              stopAtMergeGateway
            );
          }
          break;
        case this.flowinglyConstants.nodeCategory.EXCLUSIVE_GATEWAY:
          this.getNextNodeIdsForExclusiveGateway(
            node.key,
            flowSchema,
            data,
            flow,
            modelerNodeIds
          );
          break;
        case this.flowinglyConstants.nodeCategory.EVENT:
        default:
          break;
      }
    });
  }

  private findMatchedGate(gates, decisionFieldValue, isCustomDb) {
    gates = gates.sort((g1, g2) => g1.order - g2.order);
    let gate = gates.find((g) => {
      const isDefault = g.isDefault;
      const conditionValue = g?.condition?.value;

      if ((!isDefault || isDefault === 'false') && conditionValue != null) {
        const conditionType = g.condition.type;

        switch (conditionType) {
          case this.flowinglyConstants.formFieldType.CHECKBOX: {
            const value = conditionValue === 'true' ? true : false;
            return decisionFieldValue === value;
          }
          case this.flowinglyConstants.formFieldType.RADIO_BUTTON_LIST:
          case this.flowinglyConstants.formFieldType.SELECT_LIST: {
            if (!decisionFieldValue) {
              return false;
            }
            let condition;
            if (isCustomDb) {
              condition = g.condition.name;
            } else {
              condition = conditionValue;
            }

            if (decisionFieldValue.includes('text":')) {
              const dfvParts = decisionFieldValue.split(':');
              const dfv = dfvParts[2]
                .trim()
                .replaceAll('"', '')
                .replace('}', '');
              return (
                condition
                  .split('|')
                  .map((value) => value && value.trim())
                  .indexOf(dfv) > -1
              );
            } else {
              return (
                condition
                  .split('|')
                  .map((value) => value && value.trim())
                  .indexOf(decisionFieldValue.trim()) > -1
              );
            }
          }
          case this.flowinglyConstants.formFieldType.NUMBER:
          case this.flowinglyConstants.formFieldType.CURRENCY:
          default:
            return this.compute(
              decisionFieldValue,
              g.condition.name,
              conditionValue
            );
        }
      }
      return false;
    });

    if (!gate) {
      gate = gates.find((g) => g.isDefault === 'true');
    }
    return gate;
  }

  private compute(leftValue, operator, rightValue) {
    if (
      isNaN(leftValue) ||
      leftValue == null ||
      leftValue === '' ||
      isNaN(rightValue) ||
      rightValue == null ||
      rightValue === ''
    )
      return false;

    leftValue = +leftValue;
    rightValue = +rightValue;

    switch (operator) {
      case '=':
        return leftValue === rightValue;
      case '!=':
        return leftValue !== rightValue;
      case '>':
        return leftValue > rightValue;
      case '>=':
        return leftValue >= rightValue;
      case '<':
        return leftValue < rightValue;
      case '<=':
        return leftValue <= rightValue;
      default:
        return false;
    }
  }

  private getDecisionFieldValueForExclusiveGateway(
    gatewayNode,
    flowSchema,
    flow,
    data
  ) {
    const decisionFieldId = gatewayNode.gateway.fieldId;
    if (data && data[decisionFieldId] != null) {
      // decision field is in current step data
      return data[decisionFieldId];
    }

    const decisionNode = this.flowinglyModelUtilityService.getNodeByKey(
      flowSchema.nodeDataArray,
      gatewayNode.selectedNodeKey
    );
    const decisionStep = this.getStepForModelerNode(decisionNode.id, flow);
    if (decisionStep && decisionStep.fields) {
      const field = decisionStep.fields.find((f) => f.name === decisionFieldId);
      return field && field.value;
    }
    return null;
  }

  private getNextNodeIdsForExclusiveGateway(
    nodeKey,
    flowSchema,
    data,
    flow,
    nodeIds
  ) {
    try {
      const gatewayNode = this.flowinglyModelUtilityService.getNodeByKey(
        flowSchema.nodeDataArray,
        nodeKey
      );
      const gateway = gatewayNode.gateway;
      const isCustomDb = !!gateway.dbName;
      const decisionFieldValue = this.getDecisionFieldValueForExclusiveGateway(
        gatewayNode,
        flowSchema,
        flow,
        data
      );
      const matchedGate = this.findMatchedGate(
        gateway.gates,
        decisionFieldValue,
        isCustomDb
      );
      if (matchedGate) {
        const nextNode = this.flowinglyModelUtilityService.getNodeByKey(
          flowSchema.nodeDataArray,
          matchedGate.routeToKey
        );
        nextNode &&
          this.addNodeIdsForNodes([nextNode], flowSchema, data, flow, nodeIds);
      }
    } catch (e) {
      console.error(
        `${e.message} in method getNextNodeIdsForExclusiveGateway`,
        nodeKey,
        flowSchema,
        data,
        flow,
        nodeIds
      );
      throw e;
    }
  }

  private multipleApproverDialog(
    form,
    flowId,
    stepId,
    validatedData,
    multipleApproversForModal,
    totalCount,
    currentIndex,
    selectedApprovers,
    nextModelerNodeIdsWithoutCrossingMergeGw
  ) {
    const nodeId = multipleApproversForModal[currentIndex].nodeId;
    const modelerNodeId = multipleApproversForModal[currentIndex].modelerNodeId;
    return this.dialogService
      .showDialog({
        template:
          'Client/runner.flow/runner.flow.form/runner.flow.multiple-approvers.dialog.tmpl.html',
        controller: 'selectApproversController',
        appendClassName: 'ngdialog-theme-plain w-500',
        scope: this.$scope,
        closeByEscape: false,
        closeByDocument: false,
        data: {
          currentStepId: stepId,
          flowId: flowId,
          nodeId: nodeId,
          stepType: multipleApproversForModal[currentIndex].stepType,
          stepName: multipleApproversForModal[currentIndex].stepName,
          numberOfApproversRequired:
            multipleApproversForModal[currentIndex].numberOfApproversRequired,
          maxNumberOfApproversRequired:
            multipleApproversForModal[currentIndex]
              .maxNumberOfApproversRequired || 0,
          numberOfApproversRequiredType:
            multipleApproversForModal[currentIndex]
              .numberOfApproversRequiredType,
          totalCount: totalCount,
          currentIndex: currentIndex
        }
      })
      .then((approvers) => {
        if (this.dialogService.isCloseModalWithCancelAction(approvers)) {
          //user closed modal by clicking on overlay (or cancel or press Esc key)
          return false;
        }

        if (approvers != null) {
          selectedApprovers.push({
            nodeId: nodeId,
            approvers: approvers,
            isNextNode: nextModelerNodeIdsWithoutCrossingMergeGw.some(
              (id) => id === modelerNodeId
            )
          });
          if (++currentIndex < totalCount) {
            return this.multipleApproverDialog(
              form,
              flowId,
              stepId,
              validatedData,
              multipleApproversForModal,
              totalCount,
              currentIndex,
              selectedApprovers,
              nextModelerNodeIdsWithoutCrossingMergeGw
            );
          } else return true;
        }
        return false;
      });
  }

  private multipleApproversDialog(
    form,
    flowId,
    stepId,
    validatedData,
    multipleApproversForModal,
    nextModelerNodeIdsWithoutCrossingMergeGw
  ) {
    const selectedApprovers = [];
    return this.multipleApproverDialog(
      form,
      flowId,
      stepId,
      validatedData,
      multipleApproversForModal,
      multipleApproversForModal.length,
      0,
      selectedApprovers,
      nextModelerNodeIdsWithoutCrossingMergeGw
    ).then((result) => {
      if (result) {
        return selectedApprovers;
      } else return null;
    });
  }

  private dynamicActorsDialog(
    stepId: Guid,
    flowId,
    dynamicActorsForModal,
    nextModelerNodeIds,
    modelerNodeId
  ) {
    return this.dialogService
      .showDialog({
        template:
          'Client/runner.flow/runner.flow.form/runner.flow.dynamic-actor.dialog.tmpl.html',
        controller: 'dynamicActorDialogController',
        appendClassName: 'ngdialog-theme-plain w-500',
        scope: this.$scope,
        data: {
          dynamicActorsForSteps: dynamicActorsForModal,
          flowId: flowId,
          stepId: stepId,
          nextModelerNodeIds: nextModelerNodeIds,
          modelerNodeId: modelerNodeId
        }
      })
      .then((assignedDynamicActors) => {
        if (
          this.dialogService.isCloseModalWithCancelAction(assignedDynamicActors)
        ) {
          //user closed modal by clicking on overlay (or cancel or press Esc key)
          return null;
        }

        return assignedDynamicActors;
      });
  }

  private completeStep({
    stepId,
    flowId,
    validatedData,
    assignedDynamicActors,
    selectedApprovers
  }: ICompleteStepPayload) {
    this.disableFooterButtons();

    const stepFiles = this.fgFileListService.getFilesForStep(stepId);
    const dataToSave = this.stepService.appendTimezoneOffsetToDates(
      validatedData.formData,
      this.stepForForm.schema
    );
    return this.flowApiService
      .completeTask(
        stepId,
        flowId,
        dataToSave,
        stepFiles,
        assignedDynamicActors,
        selectedApprovers
      )
      .then(
        () => {
          // Only carry on if task was able to be completed - note we would have already
          // refreshed the flow lists at this point
          this.fgFileListService.removeFileControlsForFlowStep(flowId, stepId);
          if (this.flowForm) {
            this.flowForm.$setPristine(); // set form to pristine then ready for next step as use same form name
          }
          this.notificationService.showSuccessToast('Step completed', 4000);

          const formFiles = this.fgFileListService.getForm();
          if (formFiles && !formFiles.isUndefined) {
            formFiles.model.state.$setPristine();
          }

          this.intercomService.trackEvent('Step Completed', {
            'flow name': this.flow.name,
            'step name': this.stepForForm.name
          });

          this.appInsightService.trackMetricIfTimerExist(
            'stepFormSubmitted',
            this.getStepFormPropsForAppInsights()
          );
        },
        () => {
          this.enableFooterButtons();
          this.notificationService.showErrorToast('Task failed', 4000);
          this.appInsightService.trackMetricIfTimerExist(
            'stepFormSubmitted',
            this.getStepFormPropsForAppInsights()
          );
        }
      );
  }

  private isElementInViewport(el) {
    const rect = el.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  private shouldNotSave(ignoreBusy: boolean) {
    return (
      !this.flowForm || // html has not initialized
      this.flowForm.$pristine || // form is unchanged
      (!ignoreBusy && this.busyService.isBusy) || // form is busy saving or something
      this.sessionService.getUser() === undefined || // no user in session?
      (this.stepForForm.isWaitingForYou as any) === false ||
      this.stepForForm.isWaitingForYou === 0
    ); // step not assigned to you
  }

  private saveFormProgress(ignoreBusy = false) {
    if (this.shouldNotSave(ignoreBusy)) {
      return this.$q.resolve();
    }

    //The user was half way through the form and moved somewhere else
    //save the form to the server
    const formData = this.stepService.getFormDataWithoutValidating(
      this.stepForForm
    );

    if (!Array.isArray(formData) || formData.length <= 0) {
      return this.$q.resolve();
    }
    this.runnerFlowsFormatter.encodeFlowFormData(
      formData,
      this.stepForForm.schema.fields,
      true
    );
    const dataToSave = this.stepService.appendTimezoneOffsetToDates(
      formData,
      this.stepForForm.schema
    );

    return this.flowApiService
      .saveFormProgress(
        this.flow.id,
        this.stepForForm.id,
        dataToSave,
        this.fgFileListService.getFilesForStep(this.stepForForm.id)
      )
      .then(() => {
        (this.stepForForm as any).LocalUpdatedDate =
          this.dateService.utcToLocal(new Date());
      });
  }

  private initializeDeferredFileUploadPromise() {
    if (!this.deferredFileUploadPromise) {
      this.deferredFileUploadPromise = [];
    }
  }

  private disableFooterButtons() {
    const deferred = this.$q.defer();
    this.initializeDeferredFileUploadPromise();
    this.deferredFileUploadPromise.push(deferred);
    this.busyService.addPromise(deferred.promise);
  }

  private enableFooterButtons() {
    this.initializeDeferredFileUploadPromise();
    this.deferredFileUploadPromise.forEach((deferred) => {
      deferred.resolve();
      this.deferredFileUploadPromise = null;
    });
  }

  private fileRemovalComplete() {
    this.enableFooterButtons();
    this.saveFormProgress();
  }

  private setFileError(event: PubSubEvent, data: IEventFileUploadResult) {
    if (data == null || data.fileControlId == null) {
      return;
    }

    // Only set the form file error to true when the event is for a field in the selected step.
    // Otherwise Runner can report validation errors incorrectly for parallel steps.
    if (
      this.stepForForm != null &&
      this.stepForForm.schema != null &&
      this.stepForForm.schema.fields != null
    ) {
      const matchingField = this.stepForForm.schema.fields.find(
        (field) => field.name === data.fileControlId
      );
      if (matchingField != null) {
        this.fgFileListService.setFileListInvalid(
          data.fileControlId,
          this.stepForForm.id,
          data.flowInstanceId,
          true
        );
      }
    }
  }

  private clearFileError(event: PubSubEvent, data: IEventFileUploadResult) {
    this.fgFileListService.setFileListInvalid(
      data.fileControlId,
      this.stepForForm.id,
      data.flowInstanceId,
      false
    );
  }

  private settingOffsettoForm() {
    if (angular.element('#flowForm_' + this.stepForForm.flowId) == undefined) {
      this.interval = this.$timeout(this.settingOffsettoForm);
      return;
    }
    this.$timeout.cancel(this.interval);
    const offSetTop = document
      .getElementById('flowForm_' + this.stepForForm.flowId)
      .getBoundingClientRect().top;
    const scrollHeight = angular.element(document).height();
    const scrollPosition =
      angular.element(window).height() + angular.element(window).scrollTop();
    if ((scrollHeight - scrollPosition) / scrollHeight === 0) {
      if (offSetTop < 0) {
        window.scrollTo(0, scrollHeight / 2);
      } else {
        window.scrollTo(0, angular.element(window).scrollTop() - offSetTop);
      }
    }
  }

  private getStepFormPropsForAppInsights() {
    const { flowinglyConstants, flow, stepForForm } = this;
    const stepTypeName =
      flowinglyConstants.stepTypeName[ActivityTaskType[stepForForm.stepType]];
    const props = {
      flowIdentifier: flow.flowIdentifier,
      stepName: stepForForm.name,
      stepType: stepForForm.stepType,
      stepTypeName
    };
    return props;
  }

  private getStepTagIds() {
    return (this.stepForForm.parsedTags || []).map(function (tag) {
      return tag.id;
    });
  }
}

class FlowFormComponent implements angular.IComponentOptions {
  bindings = {
    flow: '<',
    stepForForm: '<',
    isMobile: '<' //show mobile view if set
  };
  templateUrl =
    'Client/runner.flow/runner.flow.form/runner.flow.form.component.tmpl.html';
  controllerAs = '$ctrl';
  controller = FlowFormComponentController;
}

angular
  .module('flowingly.runner.flow')
  .component('flowForm', new FlowFormComponent());

export interface ICompleteStepPayload {
  form;
  stepId: Guid;
  flowId: Guid;
  validatedData: IFormValidationResult;
  assignedDynamicActors: unknown[];
  selectedApprovers: unknown[];
}
